/**
 * useFirmCalendarEvents — fetches and returns firm-calendar event occurrences
 * for a visible date window.  Used by the main Schedule views (month, day,
 * week) to overlay admin-managed events like holidays and all-staff meetings.
 *
 * Backend already explodes recurring events into concrete occurrences within
 * the requested [start, end] range, so the consumer just keys by date string.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d;
  // Build YYYY-MM-DD in local time (avoid UTC drift for date-only fields)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const useFirmCalendarEvents = (rangeStart, rangeEnd) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const startStr = fmtDate(rangeStart);
  const endStr = fmtDate(rangeEnd);

  useEffect(() => {
    if (!startStr || !endStr) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/firm-calendar/events`, { params: { start: startStr, end: endStr } })
      .then((resp) => { if (!cancelled) setEvents(resp.data?.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startStr, endStr]);

  // Group occurrences by YYYY-MM-DD for quick lookup.
  //
  // If an event has `observe_weekend_shift` on AND the occurrence lands on
  // Sat/Sun, we ALSO append a synthetic "observed" occurrence to the shifted
  // weekday (Sat→Fri, Sun→Mon). The original chip stays on the real date with
  // an `_observance_note` so it can be rendered grayed out with "(Observed X)".
  const byDate = useMemo(() => {
    const m = new Map();
    const add = (key, ev) => {
      if (!key) return;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ev);
    };

    const shiftObservance = (ymd) => {
      // ymd is YYYY-MM-DD; avoid UTC drift by constructing local Date
      const [y, mo, d] = ymd.split('-').map(Number);
      const dt = new Date(y, mo - 1, d);
      const wd = dt.getDay(); // 0=Sun .. 6=Sat
      if (wd === 6) { dt.setDate(dt.getDate() - 1); }
      else if (wd === 0) { dt.setDate(dt.getDate() + 1); }
      else { return null; }
      return fmtDate(dt);
    };

    const weekdayName = (ymd) => {
      const [y, mo, d] = ymd.split('-').map(Number);
      return ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(y, mo - 1, d).getDay()];
    };

    for (const e of events) {
      const realDate = e.occurrence_date;
      if (!realDate) continue;
      const shifted = e.observe_weekend_shift ? shiftObservance(realDate) : null;
      if (shifted) {
        // Real date → render as "shadow" (actual calendar date, grayed)
        add(realDate, {
          ...e,
          _is_shadow: true,
          _observed_on: shifted,
        });
        // Observed (shifted) date → primary chip, labeled "(Observed)"
        add(shifted, {
          ...e,
          _is_observed: true,
          _actual_date: realDate,
          _actual_weekday: weekdayName(realDate),
        });
      } else {
        add(realDate, e);
      }
    }
    return m;
  }, [events]);

  return { events, loading, byDate, getForDate: (d) => byDate.get(fmtDate(d)) || [] };
};

export default useFirmCalendarEvents;
